import { useTranslation } from 'react-i18next';
import { ArrowLeftRight } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface IntercompanyOptionMarkerProps {
  /**
   * The organization's `linked_company_id` — set means this counterparty IS one
   * of the tenant's own legal entities (identity link to a `companies` row).
   */
  linkedCompanyId: string | null | undefined;
  /**
   * The picker-level multi-company gate, resolved ONCE per picker instance with
   * {@link useIntercompanyMarkerGate} — never per row.
   */
  enabled: boolean;
  className?: string;
}

/**
 * Row-level "this is one of our own companies" marker for an organization
 * option inside a picker dropdown (organization, counterparty, vendor).
 *
 * Deliberately a 14px glyph rather than a `StatusChip`: an option row already
 * spends its trailing slot on a muted domain / vendor code, and a chip with a
 * label competes for that width and enlarges every row (visible at 375px).
 * Colour is not the only carrier of meaning — the glyph is the same
 * `ArrowLeftRight` {@link IntercompanyBadge} uses on the document itself, and
 * the fact is named in an `sr-only` label plus a `title` tooltip.
 *
 * Renders nothing for an external organization, and nothing at all in a
 * single-company tenant (`enabled` false). See docs/erp/intercompany-design.md.
 */
export function IntercompanyOptionMarker({
  linkedCompanyId,
  enabled,
  className,
}: IntercompanyOptionMarkerProps) {
  const { t } = useTranslation('common');

  if (!enabled || !linkedCompanyId) {
    return null;
  }

  const label = t('multiCompany.internalCompany');

  return (
    <span className={cn('ms-1 inline-flex shrink-0 items-center', className)} title={label}>
      <ArrowLeftRight className="h-3.5 w-3.5 text-info" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </span>
  );
}
